import moment from "moment";
import { STATUS_COLORS } from "../services.js";
import { CALENDAR_CONFIG } from "./calendarConstants.js";
import {
    toAppTimezone,
    toAppTimezoneDate,
    convertTimezone,
    convertTimezoneDate,
    setTimeInTimezone,
    startOfDayInTimezone,
    addDaysInTimezone,
} from "./timezoneUtils.js";

const SUPER_ADMIN = 1;
const ADMIN = 2;
const PROJECT_MANAGER = 6;

/**
 * Builds sanitized title HTML and plain text
 */
export const buildTitles = (rawTitle) => {
    const DOMPurify = require('dompurify');
    const safe = DOMPurify.sanitize(rawTitle || "");
    // No need to strip event-time spans anymore since titles are now plain text
    const plain = (rawTitle || "").trim();
    return { titleHtml: safe, titlePlain: plain };
};

/**
 * Calculate total days for a leave request
 */
const calculateTotalDays = (start, end, startTime, endTime, tz) => {
    try {
        if (!start || !end) return 0;

        const startDateTime = moment(startOfDayInTimezone(start, tz));
        const endDateTime = moment(startOfDayInTimezone(end, tz));

        if (startDateTime.isSame(endDateTime, 'day') && startTime != null && endTime != null) {
            // Same day: calculate based on time difference
            const startWithTime = moment(setTimeInTimezone(start, parseInt(startTime, 10), 0, 0, tz));
            const endWithTime = moment(setTimeInTimezone(end, parseInt(endTime, 10), 0, 0, tz));

            const duration = moment.duration(endWithTime.diff(startWithTime));
            const numberOfHours = duration.asHours();

            if (numberOfHours > 0) {
                const numberOfDaysDecimal = Math.min(numberOfHours / 8, 1); // 8-hour workday, cap at 1 day
                return Number(numberOfDaysDecimal.toFixed(2));
            }
            return 0;
        } else {
            // Different days: count inclusive days
            const duration = moment.duration(endDateTime.diff(startDateTime));
            return Math.ceil(duration.asDays()) + 1; // Inclusive
        }
    } catch (error) {
        console.error("Error calculating total days:", error);
        return 0;
    }
};

/**
 * Process leaves into calendar events
 * @param {Array} leaves - Array of leave objects
 * @param {Object} user - Current user object
 * @param {string} tz - Timezone string
 * @param {string} dummyImage - Default profile image path
 * @param {Array} selectedUserIds - Optional array of user IDs to filter leaves by
 */
export const processLeaves = (leaves, user, tz, dummyImage, selectedUserIds = null) => {
    const out = [];
    if (!leaves) return out;

    for (const leave of leaves) {
        // Filter by selectedUserIds if provided
        if (selectedUserIds !== null && selectedUserIds !== undefined) {
            const leaveUserId = leave.user?._id?.toString();
            // Convert selectedUserIds to strings for comparison
            const selectedUserIdsStr = selectedUserIds.map(id => id?.toString()).filter(Boolean);
            if (!leaveUserId || selectedUserIdsStr.length === 0 || !selectedUserIdsStr.includes(leaveUserId)) {
                continue;
            }
        }
        const fullName = leave.user?.first_name
            ? `${leave.user.first_name} ${leave.user.middle_name && leave.user.middle_name !== "null" ? leave.user.middle_name + " " : ""}${leave.user.last_name || ""}`.trim()
            : leave.user?.company_first_name
                ? `${leave.user.company_first_name} ${leave.user.company_last_name || ""}`.trim()
                : `${leave.user?.company_name || ""} (Company)`;

        const startDate = new Date(leave.start_date);
        const endDate = new Date(leave.end_date);
        const issueDate = new Date(leave.created_at);

        const totalDays = calculateTotalDays(
            leave.start_date,
            leave.end_date,
            leave.start_time,
            leave.end_time,
            tz
        );

        let eventStart = startOfDayInTimezone(startDate, tz);
        let eventEnd = startOfDayInTimezone(addDaysInTimezone(leave.end_date, 1, tz), tz);
        const hasTime = leave.start_time != null && leave.end_time != null;

        if (hasTime) {
            eventStart = setTimeInTimezone(leave.start_date, parseInt(leave.start_time, 10), 0, 0, tz);
            eventEnd = setTimeInTimezone(leave.end_date, parseInt(leave.end_time, 10), 0, 0, tz);
        }

        const title = `${leave.user?._id?.toString() === user._id?.toString() ? "" : `${fullName} - `}${leave.type_of_leave?.leave_type || "Time Off"}${totalDays > 1 ? ` (${totalDays} days)` : ""}`;
        const { titleHtml, titlePlain } = buildTitles(title);

        out.push({
            title,
            titleHtml,
            titlePlain,
            start: eventStart,
            end: eventEnd,
            backgroundColor: STATUS_COLORS[leave.status]?.backgroundColor || "#95a5a6",
            color: STATUS_COLORS[leave.status]?.color || "#FFFFFF",
            borderColor: STATUS_COLORS[leave.status]?.borderColor || "#FFFFFF",
            fullBorderColor: STATUS_COLORS[leave.status]?.fullBorderColor || "#CCC",
            profileImage: leave.user?.profile_image ? `/Uploads/lowProfileImage/${leave.user.profile_image}` : dummyImage,
            ...(leave.user?._id?.toString() === user._id?.toString() || [SUPER_ADMIN, ADMIN, PROJECT_MANAGER].includes(user.userType) ? {
                description: leave.note,
                status: leave.status,
                note: leave.note,
            } : {}),
            user: fullName,
            userId: leave.user._id,
            typeOfLeave: leave.type_of_leave.leave_type,
            issueDate: issueDate,
            totalDays: totalDays,
            startDate: new Date(leave.start_date).toLocaleDateString("en-US"),
            endDate: new Date(leave.end_date).toLocaleDateString("en-US"),
            startTime: leave.start_time || null,
            endTime: leave.end_time || null,
            leaveId: leave._id,
            eventType: "Leave",
            allDay: !hasTime,
        });
    }
    return out;
};

/**
 * Process internal events into calendar events
 */
export const processInternalEvents = (eventsData, selectedUsers, user, tz, dummyImage) => {
    const out = [];
    if (!eventsData) return out;

    const selectedUsersSet = new Set(selectedUsers.map(id => id?.toString()).filter(Boolean));
    for (const event of eventsData) {
        const hasMatchingAttendee = event.event_attendees?.some((attendee) =>
            selectedUsersSet.has(attendee.user?._id?.toString())
        );
        if (selectedUsers.length === 0 || hasMatchingAttendee) {
            const organizerName = event.event_organizer?.first_name
                ? `${event.event_organizer.first_name} ${event.event_organizer.last_name || ""}`.trim()
                : event.event_organizer?.company_name || "Unknown Organizer";

            let eventStart = toAppTimezoneDate(event.event_date, tz, true);
            let eventEnd = toAppTimezoneDate(event.event_date, tz, true);
            const hasTime = event.start_date_time != null && event.end_date_time != null;

            if (hasTime) {
                eventStart = setTimeInTimezone(
                    event.event_date,
                    moment(event.start_date_time).hour(),
                    moment(event.start_date_time).minute(),
                    0,
                    tz
                );

                eventEnd = setTimeInTimezone(
                    event.event_date,
                    moment(event.end_date_time).hour(),
                    moment(event.end_date_time).minute(),
                    0,
                    tz
                );
            }

            const displayStartTime = hasTime ? moment(event.start_date_time).format('h:mm A') : null;
            const plainTitle = event.event_name;
            const { titleHtml, titlePlain } = buildTitles(plainTitle);

            out.push({
                id: event._id.toString(),
                title: plainTitle,
                titleHtml,
                titlePlain,
                displayStartTime,
                start: eventStart,
                end: eventEnd,
                backgroundColor: STATUS_COLORS.Event.backgroundColor,
                borderColor: STATUS_COLORS.Event.borderColor,
                fullBorderColor: STATUS_COLORS.Event?.fullBorderColor || "#CCC",
                color: STATUS_COLORS.Event.color,
                profileImage: event.event_organizer?.profile_image ? `/Uploads/lowProfileImage/${event.event_organizer.profile_image}` : dummyImage,
                description: event.event_description || "No description",
                eventType: "Event",
                source: 'local',
                allDay: !hasTime,
                eventName: event.event_name,
                eventLocation: event.event_location,
                eventOrganizer: organizerName,
                eventOrganizerId: event.event_organizer?._id || user._id,
                eventAttendees: event.event_attendees ? event.event_attendees.map(attendee => ({
                    user: {
                        _id: attendee.user?._id || attendee.user,
                        firstName: attendee.user?.first_name || "",
                        lastName: attendee.user?.last_name || "",
                        middleName: attendee.user?.middle_name || "",
                        companyName: attendee.user?.company_name || "",
                        companyFirstName: attendee.user?.company_first_name || "",
                        companyLastName: attendee.user?.company_last_name || "",
                        email: attendee.user?.email || "",
                        profileImage: attendee.user?.profile_image ? attendee.user.profile_image : dummyImage,
                    },
                    status: attendee.status || "pending",
                    reason: attendee.reason || null,
                    responseDate: attendee.response_date ? moment(attendee.response_date).format("YYYY-MM-DD HH:mm") : null,
                    additionalNotes: attendee.additional_notes || null,
                })) : [],
                externalAttendees: event.external_attendees ? event.external_attendees.map(attendee => ({
                    email: attendee.email || "",
                    name: attendee.name || "",
                    status: attendee.status || "pending",
                    reason: attendee.reason || null,
                    responseDate: attendee.response_date ? moment(attendee.response_date).format("YYYY-MM-DD HH:mm") : null,
                    additionalNotes: attendee.additional_notes || null,
                })) : [],
                eventBanner: event.event_banner ? `/Uploads/${event.event_banner}` : null,
                eventIsVirtual: event.event_is_virtual,
                eventMeetingLink: event.event_meeting_link || null,
                repeatType: event.repeat_type,
                startTime: event.start_date_time ? moment(event.start_date_time).format("HH:mm") : null,
                endTime: event.end_date_time ? moment(event.end_date_time).format("HH:mm") : null,
                startDate: new Date(event.event_date).toLocaleDateString("en-US"),
                endDate: event.end_date ? new Date(event.end_date).toLocaleDateString("en-US") : null,
                _id: event._id,
            });
        }
    }
    return out;
};

/**
 * Calculate visible date range based on calendar view and current date
 * Returns start and end dates with buffer for smooth scrolling
 */
const calculateVisibleDateRange = (currentDate, view, tz) => {
    const baseDate = moment.tz(currentDate, tz);
    let startDate, endDate;
    
    switch (view) {
        case 'month':
            // Month view: show current month + 1 month buffer on each side
            startDate = baseDate.clone().startOf('month').subtract(1, 'month');
            endDate = baseDate.clone().endOf('month').add(1, 'month');
            break;
        case 'week':
            // Week view: show current week + 2 weeks buffer on each side
            startDate = baseDate.clone().startOf('week').subtract(2, 'weeks');
            endDate = baseDate.clone().endOf('week').add(2, 'weeks');
            break;
        case 'day':
            // Day view: show current day + 1 month buffer on each side
            startDate = baseDate.clone().startOf('day').subtract(1, 'month');
            endDate = baseDate.clone().endOf('day').add(1, 'month');
            break;
        default:
            // Default: show current month + 1 month buffer
            startDate = baseDate.clone().startOf('month').subtract(1, 'month');
            endDate = baseDate.clone().endOf('month').add(1, 'month');
    }
    
    return {
        startYear: startDate.year(),
        endYear: endDate.year(),
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
    };
};

/**
 * Process birthdays into calendar events
 * Only generates events for the visible date range plus buffer
 */
export const processBirthdays = (userProfiles, tz, dummyImage, currentDate = null, view = 'month') => {
    const out = [];
    const uniqueBirthdays = new Set();
    if (!userProfiles) return out;

    // Calculate visible date range if currentDate and view are provided
    let dateRange = null;
    if (currentDate && view) {
        dateRange = calculateVisibleDateRange(currentDate, view, tz);
    }
    
    for (const userProfile of userProfiles) {
        if (userProfile.is_active === false) continue;
        
        const dob = userProfile.dob ? new Date(userProfile.dob) : null;
        if (!dob || isNaN(dob)) continue;

        const fullName = userProfile.first_name
            ? `${userProfile.first_name} ${userProfile.middle_name && userProfile.middle_name !== "null" ? userProfile.middle_name + " " : ""}${userProfile.last_name || ""}`.trim()
            : userProfile.company_first_name
                ? `${userProfile.company_first_name} ${userProfile.company_last_name || ""}`.trim()
                : `${userProfile.company_name || ""} (Company)`;

        const birthYear = dob.getFullYear();
        
        // Determine year range to generate
        let startYear, endYear;
        if (dateRange) {
            // Only generate for visible range
            startYear = Math.max(birthYear, dateRange.startYear);
            endYear = Math.min(birthYear + CALENDAR_CONFIG.BIRTHDAY_MAX_AGE, dateRange.endYear);
        } else {
            // Fallback: generate for current year + BIRTHDAY_FUTURE_YEARS years (backward compatibility)
            const currentYear = new Date().getFullYear();
            startYear = birthYear;
            endYear = currentYear + CALENDAR_CONFIG.BIRTHDAY_FUTURE_YEARS;
        }
        
        for (let year = startYear; year <= endYear; year++) {
            const age = year - birthYear;
            const birthday = new Date(Date.UTC(year, dob.getMonth(), dob.getDate()));
            const birthdayDate = moment.tz(birthday, tz);
            const birthdayInIST = birthdayDate.format(CALENDAR_CONFIG.DATE_FORMAT);
            
            // Skip if outside visible range (when dateRange is provided)
            if (dateRange) {
                if (birthdayDate.isBefore(dateRange.startDate, 'day') || birthdayDate.isAfter(dateRange.endDate, 'day')) {
                    continue;
                }
            }
            
            const title = `🎂 ${fullName}'s Birthday 🎉`;
            const { titleHtml, titlePlain } = buildTitles(title);

            if (!uniqueBirthdays.has(`${birthdayInIST}-${fullName}`)) {
                out.push({
                    title,
                    titleHtml,
                    titlePlain,
                    date: birthdayInIST,
                    start: birthdayDate.toDate(),
                    end: birthdayDate.toDate(),
                    backgroundColor: STATUS_COLORS.Birthday.backgroundColor,
                    color: STATUS_COLORS.Birthday.color,
                    borderColor: STATUS_COLORS.Birthday.borderColor,
                    fullBorderColor: STATUS_COLORS.Birthday?.fullBorderColor || "#CCC",
                    profileImage: userProfile.profile_image ? `/Uploads/lowProfileImage/${userProfile.profile_image}` : dummyImage,
                    description: `Celebrate ${fullName}'s birthday! 🎂🎁`,
                    eventType: "Birthday",
                    allDay: true,
                    age: age,
                });
                uniqueBirthdays.add(`${birthdayInIST}-${fullName}`);
            }
        }
    }
    return out;
};

/**
 * Process anniversaries into calendar events
 * Only generates events for the visible date range plus buffer
 */
export const processAnniversaries = (userProfiles, tz, dummyImage, currentDate = null, view = 'month') => {
    const out = [];
    const uniqueAnniversaries = new Set();
    if (!userProfiles) return out;

    // Calculate visible date range if currentDate and view are provided
    let dateRange = null;
    if (currentDate && view) {
        dateRange = calculateVisibleDateRange(currentDate, view, tz);
    }
    
    for (const userProfile of userProfiles) {
        if (userProfile.is_active === false) continue;
        
        const joinedDate = userProfile.joinedDate ? new Date(userProfile.joinedDate) : null;
        if (!joinedDate || isNaN(joinedDate)) continue;

        const fullName = userProfile.first_name
            ? `${userProfile.first_name} ${userProfile.middle_name && userProfile.middle_name !== "null" ? userProfile.middle_name + " " : ""}${userProfile.last_name || ""}`.trim()
            : userProfile.company_first_name
                ? `${userProfile.company_first_name} ${userProfile.company_last_name || ""}`.trim()
                : `${userProfile.company_name || ""} (Company)`;

        const joinedYear = joinedDate.getFullYear();
        
        // Determine year range to generate
        let startYear, endYear;
        if (dateRange) {
            // Only generate for visible range (starting from first anniversary)
            startYear = Math.max(joinedYear + 1, dateRange.startYear);
            endYear = Math.min(joinedYear + CALENDAR_CONFIG.ANNIVERSARY_FUTURE_YEARS, dateRange.endYear);
        } else {
            // Fallback: generate for current year + ANNIVERSARY_FUTURE_YEARS years (backward compatibility)
            const currentYear = new Date().getFullYear();
            startYear = joinedYear + 1;
            endYear = currentYear + CALENDAR_CONFIG.ANNIVERSARY_FUTURE_YEARS;
        }
        
        for (let year = startYear; year <= endYear; year++) {
            const yearsSinceJoined = year - joinedYear;
            const anniversary = new Date(Date.UTC(year, joinedDate.getMonth(), joinedDate.getDate()));
            const anniversaryDate = moment.tz(anniversary, tz);
            const anniversaryInIST = anniversaryDate.format(CALENDAR_CONFIG.DATE_FORMAT);

            // Skip if outside visible range (when dateRange is provided)
            if (dateRange) {
                if (anniversaryDate.isBefore(dateRange.startDate, 'day') || anniversaryDate.isAfter(dateRange.endDate, 'day')) {
                    continue;
                }
            }

            if (yearsSinceJoined >= 1 && !uniqueAnniversaries.has(`${anniversaryInIST}-${fullName}`)) {
                const yearText = yearsSinceJoined === 1 ? "1st" : yearsSinceJoined === 2 ? "2nd" : yearsSinceJoined === 3 ? "3rd" : `${yearsSinceJoined}th`;
                const title = `🎁 ${fullName}'s ${yearText} Work Anniversary 🎉`;
                const { titleHtml, titlePlain } = buildTitles(title);
                out.push({
                    title,
                    titleHtml,
                    titlePlain,
                    date: anniversaryInIST,
                    start: anniversaryDate.toDate(),
                    end: anniversaryDate.toDate(),
                    backgroundColor: STATUS_COLORS.Anniversary.backgroundColor,
                    color: STATUS_COLORS.Anniversary.color,
                    borderColor: STATUS_COLORS.Anniversary.borderColor,
                    fullBorderColor: STATUS_COLORS.Anniversary?.fullBorderColor || "#CCC",
                    profileImage: userProfile.profile_image ? `/Uploads/lowProfileImage/${userProfile.profile_image}` : dummyImage,
                    description: `Celebrating ${fullName}'s ${yearText} work anniversary! 🎈`,
                    eventType: "Anniversary",
                    allDay: true,
                    years: yearsSinceJoined,
                });
                uniqueAnniversaries.add(`${anniversaryInIST}-${fullName}`);
            }
        }
    }
    return out;
};

/**
 * Process Google events into calendar events
 */
export const processGoogleEvents = (selectedGoogleEvents, calendarUsers, user, tz, dummyImage) => {
    const out = [];
    if (!Array.isArray(selectedGoogleEvents) || selectedGoogleEvents.length === 0) return out;

    for (const { events: userEvents, userId, calendarEmail } of selectedGoogleEvents) {
        const calendarUser = calendarUsers.find((u) => u.userId === userId);
        const userName = calendarUser?.username || 'Unknown';

        userEvents.forEach((event) => {
            const start = event.start?.dateTime || event.start?.date;
            const end = event.end?.dateTime || event.end?.date;
            if (!start || !end) {
                console.warn(`Skipping Google event for user ${userId}: Invalid start or end date`, event);
                return;
            }

            const isAllDay = !event.start.dateTime;
            const startDate = toAppTimezone(start, tz, isAllDay);
            const endDate = toAppTimezone(end, tz, isAllDay);
            const displayStartTime = event.start.dateTime ? moment(event.start.dateTime).format('h:mm A') : null;
            const baseTitle = event.summary || 'Google Calendar Event';
            const plainTitle = userId === user._id
                ? baseTitle
                : `${userName} - ${baseTitle}`;
            const { titleHtml, titlePlain } = buildTitles(plainTitle);

            out.push({
                title: plainTitle,
                titleHtml,
                titlePlain,
                displayStartTime,
                start: startDate.toDate(),
                end: endDate.toDate(),
                backgroundColor: STATUS_COLORS.GoogleEvent.backgroundColor,
                color: STATUS_COLORS.GoogleEvent.color,
                borderColor: STATUS_COLORS.GoogleEvent.borderColor,
                fullBorderColor: STATUS_COLORS.GoogleEvent?.fullBorderColor || "#CCC",
                profileImage: calendarUser?.photoImage ? `/Uploads/lowProfileImage/${calendarUser.photoImage}` : dummyImage,
                description: event.description,
                eventType: 'GoogleEvent',
                allDay: !event.start.dateTime,
                source: 'google',
                calendarEmail,
                extendedProps: {
                    googleEventId: event.id,
                    source: 'google',
                    location: event.location || null,
                    organizer: event.organizer?.displayName || event.organizer?.email || null,
                    organizerEmail: event.organizer?.email || null,
                    attendees: event.attendees?.map((attendee) => ({
                        email: attendee.email,
                        displayName: attendee.displayName || attendee.email,
                        responseStatus: attendee.responseStatus || 'pending',
                        organizer: attendee.organizer || false,
                        optional: attendee.optional || false,
                        imageUrl: attendee.imageUrl || null,
                    })) || [],
                    hangoutLink: event.hangoutLink || null,
                    startTime: event.start.dateTime ? moment(event.start.dateTime).format('HH:mm') : null,
                    endTime: event.end.dateTime ? moment(event.end.dateTime).format('HH:mm') : null,
                    startDate: startDate.format(CALENDAR_CONFIG.DATE_FORMAT),
                    endDate: endDate.format(CALENDAR_CONFIG.DATE_FORMAT),
                    recurrence: event.recurrence || [],
                    recurringEventId: event.recurringEventId || null,
                    attachments: event.attachments?.map((attachment) => ({
                        fileUrl: attachment.fileUrl,
                        title: attachment.title,
                        mimeType: attachment.mimeType,
                        iconLink: attachment.iconLink,
                        fileId: attachment.fileId,
                    })) || [],
                    conferenceData: event.conferenceData ? {
                        entryPoints: event.conferenceData.entryPoints?.map((entry) => ({
                            entryPointType: entry.entryPointType,
                            uri: entry.uri,
                            label: entry.label,
                            pin: entry.pin || null,
                            regionCode: entry.regionCode || null,
                        })) || [],
                        conferenceSolution: event.conferenceData.conferenceSolution ? {
                            key: event.conferenceData.conferenceSolution.key,
                            name: event.conferenceData.conferenceSolution.name,
                            iconUri: event.conferenceData.conferenceSolution.iconUri,
                        } : null,
                        conferenceId: event.conferenceData.conferenceId || null,
                    } : null,
                    workingLocationType: event.workingLocationType || null,
                    eventType: event.eventType || 'default',
                    reminders: event.reminders || { useDefault: true, overrides: [] },
                    visibility: event.visibility || 'default',
                    transparency: event.transparency || 'opaque',
                    iCalUID: event.iCalUID || null,
                    userId,
                    backgroundColor: STATUS_COLORS.GoogleEvent.backgroundColor,
                },
            });
        });
    }
    return out;
};

/**
 * Process Microsoft events into calendar events
 */
export const processMicrosoftEvents = (selectedMicrosoftEvents, calendarUsers, user, tz, dummyImage) => {
    const out = [];
    if (!Array.isArray(selectedMicrosoftEvents) || selectedMicrosoftEvents.length === 0) return out;

    for (const { events: userEvents, userId } of selectedMicrosoftEvents) {
        const calendarUser = calendarUsers.find((u) => u.userId === userId);
        const userName = calendarUser?.username || 'Unknown';

        userEvents.forEach((event) => {
            const start = event.start?.dateTime || event.start?.date;
            const end = event.end?.dateTime || event.end?.date;
            if (!start || !end) return;

            const startTimeZone = event.start?.timeZone === 'UTC' ? tz : event.start?.timeZone || tz;
            const endTimeZone = event.end?.timeZone === 'UTC' ? tz : event.end?.timeZone || tz;
            
            if (event.recurrence) {
                const instances = event.instances || [];
                instances.forEach((instance) => {
                    const start = instance.start?.dateTime || instance.start?.date;
                    const end = instance.end?.dateTime || instance.end?.date;
                    if (!start || !end) return;

                    const startTimeZone = instance.start?.timeZone || tz;
                    const endTimeZone = instance.end?.timeZone || tz;
                    const isAllDay = !instance.start.dateTime;

                    const startMoment = convertTimezone(start, startTimeZone, tz, isAllDay);
                    const endMoment = convertTimezone(end, endTimeZone, tz, isAllDay);

                    const displayStartTime = instance.start.dateTime ? moment(startMoment).format('h:mm A') : null;
                    const baseTitle = instance.summary || event.summary || 'Microsoft Calendar Event';
                    const plainTitle = userId === user._id
                        ? baseTitle
                        : `${userName} - ${baseTitle}`;
                    const { titleHtml, titlePlain } = buildTitles(plainTitle);

                    out.push({
                        title: plainTitle,
                        titleHtml,
                        titlePlain,
                        displayStartTime,
                        start: startMoment.toDate(),
                        end: endMoment.toDate(),
                        backgroundColor: STATUS_COLORS.MicrosoftEvent.backgroundColor,
                        color: STATUS_COLORS.MicrosoftEvent.color,
                        borderColor: STATUS_COLORS.MicrosoftEvent.borderColor,
                        fullBorderColor: STATUS_COLORS.MicrosoftEvent?.fullBorderColor || "#CCC",
                        profileImage: calendarUser?.photoImage ? `/Uploads/lowProfileImage/${calendarUser.photoImage}` : dummyImage,
                        description: instance.description || event.description || null,
                        eventType: 'MicrosoftEvent',
                        allDay: instance.isAllDay || false,
                        source: 'microsoft',
                        calendarEmail: instance.calendarEmail || null,
                        isCancelled: instance.isCancelled || false,
                        teamsLink: instance.teamsLink || event.teamsLink || null,
                        reminders: instance.reminders || { isReminderOn: false, reminderMinutesBeforeStart: 15 },
                        showAs: instance.showAs || event.showAs || 'busy',
                        importance: instance.importance || event.importance || 'normal',
                        sensitivity: instance.sensitivity || event.sensitivity || 'normal',
                        isOnlineMeeting: instance.isOnlineMeeting || false,
                        onlineMeetingProvider: instance.onlineMeetingProvider || event.onlineMeetingProvider || 'unknown',
                        allowNewTimeProposals: (instance.allowNewTimeProposals ?? event.allowNewTimeProposals) || true,
                        isDraft: (instance.isDraft ?? event.isDraft) || false,
                        hideAttendees: (instance.hideAttendees ?? event.hideAttendees) || false,
                        extendedProps: {
                            microsoftEventId: event.id,
                            source: 'microsoft',
                            etag: instance.etag,
                            location: instance.location || null,
                            organizer: instance.organizer
                                ? { email: instance.organizer.email, displayName: instance.organizer.displayName }
                                : event.organizer
                                    ? { email: event.organizer.email, displayName: event.organizer.displayName }
                                    : { email: 'Unknown', displayName: 'Unknown' },
                            attendees: instance.attendees?.map((attendee) => ({
                                email: attendee.email,
                                displayName: attendee.displayName || attendee.email,
                                responseStatus: attendee.responseStatus || 'pending',
                                type: attendee.type || 'required',
                                statusTime: attendee.statusTime || null,
                                imageUrl: attendee.imageUrl || null,
                            })) || [],
                            teamsLink: instance.teamsLink || event.teamsLink || null,
                            startTime: instance.start.dateTime ? moment(startMoment).format('HH:mm') : null,
                            endTime: instance.end.dateTime ? moment(endMoment).format('HH:mm') : null,
                            startDate: moment(startMoment).format(CALENDAR_CONFIG.DATE_FORMAT),
                            endDate: moment(endMoment || startMoment).format(CALENDAR_CONFIG.DATE_FORMAT),
                            recurrence: event.recurrence || null,
                            recurringEventId: event.recurringEventId || event.id,
                            instances: [],
                            attachments: [],
                            conferenceData: null,
                            userId,
                            responseStatus: instance.responseStatus || null,
                            microsoftUserId: instance.microsoftUserId || event.microsoftUserId,
                        },
                    });
                });
            } else {
                const startMoment = event.start?.timeZone === 'UTC' ? moment.utc(start).tz(startTimeZone) : moment.tz(start, startTimeZone);
                const endMoment = event.end?.timeZone === 'UTC' ? moment.utc(end).tz(endTimeZone) : moment.tz(end, endTimeZone);
                const displayStartTime = event.start.dateTime ? moment(startMoment).format('h:mm A') : null;
                const baseTitle = event.summary || 'Microsoft Calendar Event';
                const plainTitle = userId === user._id
                    ? baseTitle
                    : `${userName} - ${baseTitle}`;
                const { titleHtml, titlePlain } = buildTitles(plainTitle);
                
                out.push({
                    title: plainTitle,
                    titleHtml,
                    titlePlain,
                    displayStartTime,
                    start: startMoment.toDate(),
                    end: endMoment.toDate(),
                    backgroundColor: STATUS_COLORS.MicrosoftEvent.backgroundColor,
                    color: STATUS_COLORS.MicrosoftEvent.color,
                    borderColor: STATUS_COLORS.MicrosoftEvent.borderColor,
                    fullBorderColor: STATUS_COLORS.MicrosoftEvent?.fullBorderColor || "#CCC",
                    profileImage: calendarUser?.photoImage ? `/Uploads/lowProfileImage/${calendarUser.photoImage}` : dummyImage,
                    description: event.description,
                    eventType: 'MicrosoftEvent',
                    allDay: event.isAllDay || false,
                    source: 'microsoft',
                    calendarEmail: event.calendarEmail || null,
                    isCancelled: event.isCancelled || false,
                    teamsLink: event.teamsLink || null,
                    reminders: event.reminders || { isReminderOn: false, reminderMinutesBeforeStart: 15 },
                    showAs: event.showAs || 'busy',
                    importance: event.importance || 'normal',
                    sensitivity: event.sensitivity || 'normal',
                    isOnlineMeeting: event.isOnlineMeeting || false,
                    onlineMeetingProvider: event.onlineMeetingProvider || 'unknown',
                    allowNewTimeProposals: event.allowNewTimeProposals || true,
                    isDraft: event.isDraft || false,
                    hideAttendees: event.hideAttendees || false,
                    extendedProps: {
                        microsoftEventId: event.id,
                        source: 'microsoft',
                        etag: event.etag,
                        location: event.location || null,
                        organizer: event.organizer
                            ? { email: event.organizer.email, displayName: event.organizer.displayName }
                            : { email: 'Unknown', displayName: 'Unknown' },
                        attendees: event.attendees?.map((attendee) => ({
                            email: attendee.email,
                            displayName: attendee.displayName || attendee.email,
                            responseStatus: attendee.responseStatus || 'pending',
                            type: attendee.type || 'required',
                            statusTime: attendee.statusTime || null,
                            imageUrl: attendee.imageUrl || null,
                        })) || [],
                        teamsLink: event.teamsLink || null,
                        startTime: event.start.dateTime ? moment(startMoment).format('HH:mm') : null,
                        endTime: event.end.dateTime ? moment(endMoment).format('HH:mm') : null,
                        startDate: moment(startMoment).format('YYYY-MM-DD'),
                        endDate: moment(endMoment || startMoment).format('YYYY-MM-DD'),
                        recurrence: event.recurrence || null,
                        recurringEventId: event.recurringEventId || null,
                        instances: event.instances?.map(instance => ({
                            id: instance.id,
                            start: {
                                dateTime: moment.tz(
                                    instance.start.dateTime,
                                    instance.start.timeZone === 'UTC' ? (instance.originalStartTimeZone || tz || 'UTC') : instance.start.timeZone || tz || 'UTC'
                                ).toDate(),
                                timeZone: instance.start.timeZone === 'UTC' ? (instance.originalStartTimeZone || tz || 'UTC') : instance.start.timeZone || tz || 'UTC',
                            },
                            end: {
                                dateTime: moment.tz(
                                    instance.end.dateTime,
                                    instance.end.timeZone === 'UTC' ? (instance.originalEndTimeZone || tz || 'UTC') : instance.end.timeZone || tz || 'UTC'
                                ).toDate(),
                                timeZone: instance.end.timeZone === 'UTC' ? (instance.originalEndTimeZone || tz || 'UTC') : instance.end.timeZone || tz || 'UTC',
                            },
                            isCancelled: instance.isCancelled || false,
                        })) || [],
                        attachments: [],
                        conferenceData: null,
                        userId,
                        responseStatus: event.responseStatus || null,
                    },
                });
            }
        });
    }
    return out;
};

/**
 * Process holidays into calendar events
 */
export const processHolidays = (holidaysData, user, tz) => {
    const out = [];
    if (!holidaysData) return out;

    const toDateOnly = (d) => moment.utc(d).format(CALENDAR_CONFIG.DATE_FORMAT);

    for (const holiday of holidaysData) {
        const appliesToUser = holiday.applies_to?.some((id) => id._id === user._id) || holiday.applies_to.length === 0;
        const baseStartStr = toDateOnly(holiday.holiday_date);
        const baseEndStr = holiday.end_date ? toDateOnly(holiday.end_date) : null;

        const startLocal = moment.tz(baseStartStr, tz).startOf('day');
        const endLocal = baseEndStr
            ? moment.tz(baseEndStr, tz).clone().add(1, 'day').startOf('day')
            : startLocal.clone().add(1, 'day');

        const recurrenceEndDate = holiday.recurrence_end_date
            ? moment.tz(toDateOnly(holiday.recurrence_end_date), tz).endOf('day').toDate()
            : null;
        const title = holiday.holiday_name;
        const { titleHtml, titlePlain } = buildTitles(title);
        
        out.push({
            id: holiday._id,
            title,
            titleHtml,
            titlePlain,
            date: startLocal.format(CALENDAR_CONFIG.DATE_FORMAT),
            start: startLocal.toDate(),
            end: endLocal.toDate(),
            recurrenceEndDate: recurrenceEndDate,
            backgroundColor: STATUS_COLORS.Holiday.backgroundColor || "#f39c12",
            color: STATUS_COLORS.Holiday.color || "#FFFFFF",
            borderColor: STATUS_COLORS.Holiday.borderColor || "#f39c12",
            fullBorderColor: STATUS_COLORS.Holiday?.fullBorderColor || "#DED0FF",
            description: holiday.description || "Holiday",
            eventType: "Holiday",
            allDay: true,
            holidayId: holiday._id,
            repeatType: holiday.repeat_type,
            repeatRule: holiday.repeat_rule,
            appliesTo: holiday.applies_to,
            isHoliday: holiday.is_holiday,
            appliesToUser: appliesToUser,
        });
    }
    return out;
};

